/**
 * Todos Page
 * BuilderBel Construction Project Management System
 */

import {
  AlertCircle,
  ArrowRightCircle,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ListTodo,
  Pencil,
  Plus,
  RefreshCw,
  RotateCcw,
  Trash2,
  UserCheck,
} from 'lucide-react';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import { TodoDrawer } from '../components/todos';
import {
  ActionMenu,
  type ActionMenuItem,
  EmptyState,
  ModuleLayout,
  PriorityBadge,
  StatusBadge,
} from '../components/ui';
import { SearchableDropdown } from '../components/forms';
import { useToast } from '../contexts/ToastContext';
import { useCurrentProjectId } from '../hooks/useProjectRedux';
import {
  useBulkAssignTodosMutation,
  useBulkCompleteTodosMutation,
  useBulkDeleteTodosMutation,
  useCompleteTodoMutation,
  useDeleteTodoMutation,
  useGetTodosQuery,
  useQuickCreateTodoMutation,
  useReopenTodoMutation,
  useConvertTodoToTaskMutation,
} from '../services/todoApi';
import type { Todo, TodoPriority, TodoScope, TodoStatus } from '../types/todo';

// ============================================================================
// CONSTANTS
// ============================================================================

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const PRIORITY_OPTIONS = [
  { value: '', label: 'All Priority' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const SCOPE_OPTIONS = [
  { value: 'personal', label: 'My To-Dos' },
  { value: 'team', label: 'Team' },
  { value: 'all', label: 'All' },
];

const SORT_OPTIONS = [
  { value: 'createdAt', label: 'Created Date' },
  { value: 'dueDate', label: 'Due Date' },
  { value: 'priority', label: 'Priority' },
  { value: 'title', label: 'Title' },
  { value: 'status', label: 'Status' },
];

// ============================================================================
// HELPERS
// ============================================================================

const getStatusVariant = (
  status: TodoStatus
): 'success' | 'warning' | 'error' | 'info' | 'neutral' => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'in_progress':
      return 'info';
    case 'cancelled':
      return 'error';
    case 'pending':
    default:
      return 'warning';
  }
};

const getPriorityVariant = (priority: TodoPriority): 'low' | 'medium' | 'high' | 'critical' => {
  if (priority === 'urgent') return 'critical';
  return priority;
};

const formatStatus = (status: TodoStatus): string => {
  switch (status) {
    case 'in_progress':
      return 'In Progress';
    default:
      return status.charAt(0).toUpperCase() + status.slice(1);
  }
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const isOverdue = (todo: Todo): boolean => {
  if (!todo.dueDate) return false;
  if (todo.status === 'completed' || todo.status === 'cancelled') return false;
  return new Date(todo.dueDate) < new Date(new Date().setHours(0, 0, 0, 0));
};

// ============================================================================
// COMPONENT
// ============================================================================

const Todos: React.FC = () => {
  const projectId = useCurrentProjectId();
  const toast = useToast();

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [scope, setScope] = useState<TodoScope>('personal');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);

  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingTodo, setEditingTodo] = useState<Todo | null>(null);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');

  // Quick add
  const [quickAddValue, setQuickAddValue] = useState('');
  const quickAddRef = useRef<HTMLInputElement>(null);

  // API Mutations
  const [completeTodo] = useCompleteTodoMutation();
  const [reopenTodo] = useReopenTodoMutation();
  const [deleteTodo] = useDeleteTodoMutation();
  const [quickCreateTodo, { isLoading: isQuickCreating }] = useQuickCreateTodoMutation();
  const [bulkComplete, { isLoading: isBulkCompleting }] = useBulkCompleteTodosMutation();
  const [bulkAssign] = useBulkAssignTodosMutation();
  const [bulkDelete, { isLoading: isBulkDeleting }] = useBulkDeleteTodosMutation();
  const [convertToTask] = useConvertTodoToTaskMutation();

  const limit = 20;

  // Query
  const { data, isLoading, isFetching, refetch } = useGetTodosQuery(
    {
      projectId: projectId || '',
      params: {
        search: search || undefined,
        status: (statusFilter as TodoStatus) || undefined,
        priority: (priorityFilter as TodoPriority) || undefined,
        scope,
        sortBy: sortBy as 'createdAt' | 'updatedAt' | 'dueDate' | 'priority' | 'title' | 'status',
        sortOrder,
        page,
        limit,
      },
    },
    { skip: !projectId }
  );

  const todos = data?.data?.todos || [];
  const pagination = data?.data?.pagination;
  const stats = data?.data?.stats;

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleCreateTodo = useCallback(() => {
    setEditingTodo(null);
    setDrawerMode('create');
    setDrawerOpen(true);
  }, []);

  const handleEditTodo = useCallback((todo: Todo) => {
    setEditingTodo(todo);
    setDrawerMode('edit');
    setDrawerOpen(true);
  }, []);

  const handleComplete = useCallback(
    async (todo: Todo) => {
      if (!projectId) return;
      try {
        await completeTodo({ projectId, todoId: todo._id }).unwrap();
        toast.success('To-do completed');
      } catch (_error) {
        toast.error('Failed to complete to-do');
      }
    },
    [projectId, completeTodo, toast]
  );

  const handleReopen = useCallback(
    async (todo: Todo) => {
      if (!projectId) return;
      try {
        await reopenTodo({ projectId, todoId: todo._id }).unwrap();
        toast.success('To-do reopened');
      } catch (_error) {
        toast.error('Failed to reopen to-do');
      }
    },
    [projectId, reopenTodo, toast]
  );

  const handleDelete = useCallback(
    async (todo: Todo) => {
      if (!projectId) return;
      if (!window.confirm(`Delete "${todo.title}"? This cannot be undone.`)) return;
      try {
        await deleteTodo({ projectId, todoId: todo._id }).unwrap();
        toast.success('To-do deleted');
        setSelectedIds(prev => {
          const next = new Set(prev);
          next.delete(todo._id);
          return next;
        });
      } catch (_error) {
        toast.error('Failed to delete to-do');
      }
    },
    [projectId, deleteTodo, toast]
  );

  const handleConvertToTask = useCallback(
    async (todo: Todo) => {
      if (!projectId) return;
      try {
        await convertToTask({
          projectId,
          todoId: todo._id,
          data: { projectId },
        }).unwrap();
        toast.success('To-do converted to task');
      } catch (_error) {
        toast.error('Failed to convert to-do to task');
      }
    },
    [projectId, convertToTask, toast]
  );

  const handleQuickAdd = useCallback(
    async (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== 'Enter') return;
      const title = quickAddValue.trim();
      if (!title || !projectId) return;

      try {
        await quickCreateTodo({ projectId, data: { title } }).unwrap();
        setQuickAddValue('');
        toast.success('To-do added');
      } catch (_error) {
        toast.error('Failed to add to-do');
      }
    },
    [quickAddValue, projectId, quickCreateTodo, toast]
  );

  // ============================================================================
  // SELECTION HANDLERS
  // ============================================================================

  const handleSelectAll = useCallback(() => {
    if (selectedIds.size === todos.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(todos.map(t => t._id)));
    }
  }, [todos, selectedIds.size]);

  const handleSelectTodo = useCallback((todoId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(todoId)) {
        next.delete(todoId);
      } else {
        next.add(todoId);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  // ============================================================================
  // BULK HANDLERS
  // ============================================================================

  const handleBulkComplete = useCallback(async () => {
    if (!projectId || selectedIds.size === 0) return;
    try {
      const result = await bulkComplete({
        projectId,
        data: { todoIds: Array.from(selectedIds) },
      }).unwrap();
      toast.success(result.message || 'To-dos completed');
      clearSelection();
    } catch (_error) {
      toast.error('Failed to complete to-dos');
    }
  }, [projectId, selectedIds, bulkComplete, toast, clearSelection]);

  const handleBulkDelete = useCallback(async () => {
    if (!projectId || selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} to-do(s)? This cannot be undone.`)) return;
    try {
      const result = await bulkDelete({
        projectId,
        data: { todoIds: Array.from(selectedIds) },
      }).unwrap();
      toast.success(result.message || 'To-dos deleted');
      clearSelection();
    } catch (_error) {
      toast.error('Failed to delete to-dos');
    }
  }, [projectId, selectedIds, bulkDelete, toast, clearSelection]);

  // ============================================================================
  // ACTION MENU BUILDER
  // ============================================================================

  const buildActionItems = useCallback(
    (todo: Todo): (ActionMenuItem | 'divider')[] => {
      const isCompleted = todo.status === 'completed';
      return [
        {
          label: 'Edit',
          icon: <Pencil className="h-4 w-4" />,
          onClick: () => handleEditTodo(todo),
        },
        isCompleted
          ? {
              label: 'Reopen',
              icon: <RotateCcw className="h-4 w-4" />,
              onClick: () => handleReopen(todo),
            }
          : {
              label: 'Complete',
              icon: <CheckCircle2 className="h-4 w-4" />,
              variant: 'success' as const,
              onClick: () => handleComplete(todo),
            },
        {
          label: 'Convert to Task',
          icon: <ArrowRightCircle className="h-4 w-4" />,
          onClick: () => handleConvertToTask(todo),
          disabled: isCompleted,
        },
        'divider',
        {
          label: 'Delete',
          icon: <Trash2 className="h-4 w-4" />,
          variant: 'danger' as const,
          onClick: () => handleDelete(todo),
        },
      ];
    },
    [handleEditTodo, handleReopen, handleComplete, handleConvertToTask, handleDelete]
  );

  // ============================================================================
  // MODULE STATS
  // ============================================================================

  const moduleStats = useMemo(
    () => [
      { title: 'Total', value: stats?.total ?? 0 },
      { title: 'Pending', value: stats?.pending ?? 0, variant: 'warning' as const },
      { title: 'Overdue', value: stats?.overdue ?? 0, variant: 'danger' as const },
      { title: 'Due Today', value: stats?.dueToday ?? 0, variant: 'info' as const },
    ],
    [stats]
  );

  // ============================================================================
  // FILTERS JSX
  // ============================================================================

  const filtersJsx = (
    <div className="flex items-center gap-2">
      <div className="w-28">
        <SearchableDropdown
          placeholder="Status"
          value={statusFilter}
          onChange={val => {
            setStatusFilter(val);
            setPage(1);
          }}
          options={STATUS_OPTIONS}
          searchable={false}
        />
      </div>
      <div className="w-28">
        <SearchableDropdown
          placeholder="Priority"
          value={priorityFilter}
          onChange={val => {
            setPriorityFilter(val);
            setPage(1);
          }}
          options={PRIORITY_OPTIONS}
          searchable={false}
        />
      </div>
      <div className="w-32">
        <SearchableDropdown
          placeholder="Scope"
          value={scope}
          onChange={val => {
            setScope(val as TodoScope);
            setPage(1);
          }}
          options={SCOPE_OPTIONS}
          searchable={false}
        />
      </div>
    </div>
  );

  // ============================================================================
  // PAGINATION JSX
  // ============================================================================

  const paginationJsx = pagination && pagination.pages > 1 && (
    <div className="flex items-center justify-between text-xs text-gray-500">
      <span>
        Showing {(pagination.page - 1) * pagination.limit + 1}–
        {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}{' '}
        to-dos
      </span>
      <div className="flex items-center space-x-1">
        <button
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={!pagination.hasPrev}
          className="p-1 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="px-2">
          {pagination.page} / {pagination.pages}
        </span>
        <button
          onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
          disabled={!pagination.hasNext}
          className="p-1 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );

  // ============================================================================
  // RENDER
  // ============================================================================

  if (!projectId) {
    return (
      <ModuleLayout
        title="To-Dos"
        description="Manage action items and to-do lists"
        icon={<ListTodo className="h-4 w-4 text-brand-600" />}
        stats={[]}
      >
        <EmptyState
          icon={ListTodo}
          title="No project selected"
          description="Select a project to view and manage to-dos."
        />
      </ModuleLayout>
    );
  }

  return (
    <>
      <ModuleLayout
        title="To-Dos"
        description="Manage action items and to-do lists for this project"
        icon={<ListTodo className="h-4 w-4 text-brand-600" />}
        primaryAction={{
          label: 'Add To-Do',
          onClick: handleCreateTodo,
          icon: <Plus className="h-3 w-3 mr-1" />,
        }}
        stats={moduleStats}
        searchValue={search}
        onSearchChange={val => {
          setSearch(val);
          setPage(1);
        }}
        filters={filtersJsx}
        sortOptions={SORT_OPTIONS}
        sortValue={sortBy}
        onSortChange={val => {
          setSortBy(val);
          setPage(1);
        }}
        sortOrder={sortOrder}
        onSortOrderChange={order => {
          setSortOrder(order);
          setPage(1);
        }}
        onRefresh={refetch}
        isLoading={isFetching}
        pagination={paginationJsx || undefined}
      >
        {/* Quick Add Input */}
        <div className="mb-3">
          <div className="relative">
            <Plus className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              ref={quickAddRef}
              type="text"
              value={quickAddValue}
              onChange={e => setQuickAddValue(e.target.value)}
              onKeyDown={handleQuickAdd}
              placeholder="Quick add — type a to-do and press Enter..."
              disabled={isQuickCreating}
              className="w-full pl-9 pr-4 py-2 text-sm border border-dashed border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 focus:bg-white transition-colors placeholder-gray-400 disabled:opacity-60"
            />
          </div>
        </div>

        {/* Bulk Action Bar */}
        {selectedIds.size > 0 && (
          <div className="mb-3 flex items-center justify-between bg-brand-50 border border-brand-200 rounded-lg px-4 py-2">
            <span className="text-sm font-medium text-brand-700">{selectedIds.size} selected</span>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleBulkComplete}
                disabled={isBulkCompleting}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded-md transition-colors disabled:opacity-50"
              >
                <Check className="h-3 w-3 mr-1" />
                Complete
              </button>
              <button
                onClick={() => toast.info('Select an assignee from the drawer')}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-md transition-colors"
              >
                <UserCheck className="h-3 w-3 mr-1" />
                Assign
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={isBulkDeleting}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-md transition-colors disabled:opacity-50"
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Delete
              </button>
              <button
                onClick={clearSelection}
                className="text-xs text-gray-500 hover:text-gray-700 ml-2"
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <LoadingSpinner />
          </div>
        ) : todos.length === 0 ? (
          <EmptyState
            icon={ListTodo}
            title="No to-dos found"
            description={
              search || statusFilter || priorityFilter
                ? 'Try adjusting your filters to see more results.'
                : 'Create your first to-do to start tracking action items.'
            }
            action={{
              label: 'Add To-Do',
              onClick: handleCreateTodo,
            }}
          />
        ) : (
          /* Table */
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="w-8 pb-2 pr-2">
                    <input
                      type="checkbox"
                      checked={selectedIds.size === todos.length && todos.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                      aria-label="Select all"
                    />
                  </th>
                  <th className="pb-2 text-left font-medium text-gray-500 text-xs">Title</th>
                  <th className="pb-2 text-left font-medium text-gray-500 text-xs w-24">
                    Priority
                  </th>
                  <th className="pb-2 text-left font-medium text-gray-500 text-xs w-32">
                    Assigned To
                  </th>
                  <th className="pb-2 text-left font-medium text-gray-500 text-xs w-28">
                    Due Date
                  </th>
                  <th className="pb-2 text-left font-medium text-gray-500 text-xs w-28">Status</th>
                  <th className="pb-2 w-10" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {todos.map(todo => {
                  const overdue = isOverdue(todo);
                  return (
                    <tr
                      key={todo._id}
                      className={`group relative hover:bg-gray-50 transition-colors ${
                        overdue ? 'border-l-2 border-red-400' : ''
                      } ${selectedIds.has(todo._id) ? 'bg-brand-50/30' : ''}`}
                    >
                      {/* Checkbox / Complete Button */}
                      <td className="py-3 pr-2">
                        <button
                          onClick={() =>
                            todo.status === 'completed' ? handleReopen(todo) : handleComplete(todo)
                          }
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                            todo.status === 'completed'
                              ? 'bg-green-500 border-green-500 text-white'
                              : 'border-gray-300 hover:border-brand-500 hover:bg-brand-50'
                          }`}
                          aria-label={todo.status === 'completed' ? 'Reopen todo' : 'Complete todo'}
                          title={
                            todo.status === 'completed' ? 'Click to reopen' : 'Click to complete'
                          }
                        >
                          {todo.status === 'completed' && <Check className="h-3 w-3" />}
                        </button>
                      </td>

                      {/* Title */}
                      <td className="py-3 pr-4">
                        <button
                          onClick={() => handleEditTodo(todo)}
                          className="text-left group/title"
                        >
                          <span
                            className={`font-medium text-gray-900 group-hover/title:text-brand-600 transition-colors ${
                              todo.status === 'completed' ? 'line-through text-gray-400' : ''
                            }`}
                          >
                            {todo.title}
                          </span>
                          {overdue && (
                            <span className="ml-2 inline-flex items-center text-[10px] text-red-500">
                              <AlertCircle className="h-3 w-3 mr-0.5" />
                              Overdue
                            </span>
                          )}
                          {todo.tags && todo.tags.length > 0 && (
                            <div className="mt-0.5 flex flex-wrap gap-1">
                              {todo.tags.slice(0, 3).map(tag => (
                                <span
                                  key={tag}
                                  className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </button>
                      </td>

                      {/* Priority */}
                      <td className="py-3 pr-4">
                        <PriorityBadge priority={getPriorityVariant(todo.priority)} />
                      </td>

                      {/* Assigned To */}
                      <td className="py-3 pr-4">
                        <span className="text-xs text-gray-600 truncate max-w-[120px] block">
                          {todo.assignedToName || '-'}
                        </span>
                      </td>

                      {/* Due Date */}
                      <td className="py-3 pr-4">
                        <span
                          className={`text-xs ${overdue ? 'text-red-500 font-medium' : 'text-gray-600'}`}
                        >
                          {formatDate(todo.dueDate)}
                        </span>
                      </td>

                      {/* Status */}
                      <td className="py-3 pr-4">
                        <StatusBadge
                          status={formatStatus(todo.status)}
                          variant={getStatusVariant(todo.status)}
                          size="sm"
                        />
                      </td>

                      {/* Actions */}
                      <td className="py-3 text-right">
                        <div className="flex items-center justify-end space-x-1">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(todo._id)}
                            onChange={() => handleSelectTodo(todo._id)}
                            className="rounded border-gray-300 text-brand-600 focus:ring-brand-500 opacity-0 group-hover:opacity-100 transition-opacity"
                            aria-label={`Select ${todo.title}`}
                          />
                          <ActionMenu
                            id={`todo-action-${todo._id}`}
                            items={buildActionItems(todo)}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </ModuleLayout>

      {/* Todo Drawer */}
      <TodoDrawer
        isOpen={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setEditingTodo(null);
        }}
        todo={editingTodo}
        mode={drawerMode}
        onSuccess={() => refetch()}
      />
    </>
  );
};

export default Todos;
